<template>
  <div class="file-preview-page">
    <!-- 顶部工具栏：选文件 / 示例 / 开关 -->
    <div class="preview-toolbar">
      <div class="toolbar-left">
        <div class="brand">
          <span class="brand-icon">📄</span>
          <div class="brand-text">
            <strong>文件在线预览</strong>
            <small>纯前端渲染 · 文件不会上传到服务器</small>
          </div>
        </div>
      </div>

      <div class="toolbar-right">
        <el-upload
          ref="uploadRef"
          class="toolbar-upload"
          action="#"
          :auto-upload="false"
          :show-file-list="false"
          :accept="acceptAttr"
          :on-change="handleFileChange"
          :disabled="loading"
        >
          <el-button type="primary" size="small" icon="el-icon-upload2" :loading="loading">
            选择文件
          </el-button>
        </el-upload>

        <el-dropdown size="small" trigger="click" @command="handleSample">
          <el-button size="small" icon="el-icon-magic-stick">
            加载示例<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="sample in samples"
              :key="sample.value"
              :command="sample.value"
            >
              {{ sample.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>

        <div class="toolbar-switch">
          <span class="switch-label">暗色</span>
          <el-switch v-model="isDark" active-color="#4b6bfb" @change="handleThemeChange" />
        </div>
      </div>
    </div>

    <!-- 主体：左文件信息 + 右预览区 -->
    <div class="preview-body">
      <aside class="preview-aside">
        <div class="aside-card">
          <div class="aside-card__title">当前文件</div>
          <template v-if="currentFileName">
            <div class="aside-file">
              <span class="aside-file__icon">{{ currentFileIcon }}</span>
              <div class="aside-file__meta">
                <div class="aside-file__name" :title="currentFileName">{{ currentFileName }}</div>
                <div class="aside-file__sub">{{ currentFileExtLabel }} · {{ currentFileSizeLabel }}</div>
              </div>
            </div>
            <div class="aside-actions">
              <el-button type="text" size="small" icon="el-icon-download" @click="downloadCurrent">
                下载原文件
              </el-button>
            </div>
          </template>
          <div v-else class="aside-empty">还没有选择文件</div>
        </div>

        <div class="aside-card">
          <div class="aside-card__title">支持的格式</div>
          <div class="tag-wall">
            <span v-for="ext in shortcutExtensions" :key="ext" class="format-tag">{{ ext }}</span>
            <span class="format-tag format-tag--more">+ 更多文本格式</span>
          </div>
          <div class="aside-tip">
            渲染在浏览器本地完成，大文件解析时会走 Web Worker，不会卡住页面。
          </div>
        </div>

        <div class="aside-card aside-card--hint">
          <div class="aside-card__title">使用提示</div>
          <ul class="hint-list">
            <li>支持拖拽文件到右侧预览区</li>
            <li>也可以直接粘贴 URL 打开在线文件</li>
            <li>老版 <code>.ppt</code> 需要额外渲染器，当前仅提示降级</li>
          </ul>
        </div>
      </aside>

      <section
        class="preview-stage"
        :class="{ 'is-dark': isDark, 'is-dragover': isDragOver, 'is-loading': loading }"
        @dragenter.prevent="isDragOver = true"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
      >
        <!-- 预览容器：File Viewer 挂载点 -->
        <div ref="viewerHost" class="viewer-host"></div>

        <!-- 空态：没选文件时盖在预览区上 -->
        <div v-if="!currentFileName" class="stage-empty">
          <div class="empty-icon">🗂️</div>
          <div class="empty-title">把 Word / Excel / PPT / PDF 拖进来</div>
          <div class="empty-sub">或者点右上角「选择文件」、下方步骤卡片</div>

          <div class="empty-actions">
            <el-upload
              class="empty-upload"
              action="#"
              :auto-upload="false"
              :show-file-list="false"
              :accept="acceptAttr"
              :on-change="handleFileChange"
            >
              <el-button type="primary" icon="el-icon-folder-opened">本地选择文件</el-button>
            </el-upload>
            <el-button icon="el-icon-link" @click="urlDialogVisible = true">打开在线文件</el-button>
          </div>

          <div class="url-row">
            <el-input
              v-model="urlInput"
              size="small"
              placeholder="粘贴文件地址，例如 https://example.com/a.docx"
              clearable
              @keyup.enter.native="openUrl"
            >
              <el-button slot="append" icon="el-icon-top-right" @click="openUrl">打开</el-button>
            </el-input>
          </div>

          <div class="empty-flow">
            <div class="flow-card">
              <div class="flow-step">1</div>
              <div class="flow-title">选择文件</div>
              <p>本地文件或在线 URL，文件始终留在浏览器内存里</p>
            </div>
            <div class="flow-arrow">→</div>
            <div class="flow-card">
              <div class="flow-step">2</div>
              <div class="flow-title">解析渲染</div>
              <p>按扩展名命中渲染器，Worker + WASM 按需加载</p>
            </div>
            <div class="flow-arrow">→</div>
            <div class="flow-card">
              <div class="flow-step">3</div>
              <div class="flow-title">工具栏操作</div>
              <p>搜索、缩放、打印、导出、下载共用一套工具栏</p>
            </div>
          </div>
        </div>

        <!-- 加载遮罩 -->
        <div v-if="loading" class="stage-loading">
          <div class="loading-dot"></div>
          <div class="loading-text">{{ loadingText }}</div>
        </div>

        <!-- 错误提示 -->
        <div v-if="errorMessage" class="stage-error">
          <div class="error-title">预览失败</div>
          <div class="error-message">{{ errorMessage }}</div>
          <div class="error-sub">
            常见原因：文件损坏、扩展名与实际内容不符，或该格式需要额外渲染器。可以换一个文件再试。
          </div>
        </div>

        <!-- 拖拽提示 -->
        <div v-if="isDragOver" class="stage-dragover-tip">松手即可预览</div>
      </section>
    </div>

    <!-- 在线 URL 打开对话框（空态之外的入口） -->
    <el-dialog
      title="打开在线文件"
      :visible.sync="urlDialogVisible"
      width="520px"
      append-to-body
      custom-class="url-dialog"
    >
      <el-input
        v-model="urlDialogInput"
        placeholder="https://example.com/xxx.docx"
        clearable
        @keyup.enter.native="confirmUrlDialog"
      />
      <div class="dialog-tip">
        地址需要允许跨域访问（CORS），否则浏览器会拦截读取。
      </div>
      <span slot="footer">
        <el-button size="small" @click="urlDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="confirmUrlDialog">打开</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 网络教学 → 文件在线预览
 *
 * 基于 flyfish-dev/file-viewer 的 core + 少量 renderer 实现纯前端文档预览：
 *   - core / renderer 均为浏览器原生 ESM，运行时按需 import（webpack 4 无法解析这些包）；
 *   - Worker / WASM / 字体等静态资源放在 public/file-viewer 下，由 scripts/copy-file-viewer-assets.js 随构建复制；
 *   - 模块文件由 scripts/prepare-file-viewer-runtime.js 在构建前铺平到 public/file-viewer/runtime；
 *   - 本地文件以 File 传入，在线文件以 URL 传入，全程不上传服务器。
 */
import {
  ensureFileViewerRuntime,
  setFileViewerRuntimeBase,
  PREVIEW_ACCEPT_ATTR,
} from './renderers'

/** 浏览器地址根 + 静态资源目录，最终形如 `/file-viewer/` */
function resolveAssetBase() {
  const { base } = process.env
  const root = !base || base === '/'
    ? '/'
    : `${base.replace(/^\/+/, '/').replace(/\/+$/, '')}/`
  return `${root}file-viewer/`
}

/** 字节数格式化 */
function formatSize(bytes) {
  if (!bytes && bytes !== 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let index = 0

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024
    index += 1
  }

  return `${value.toFixed(value >= 100 || index === 0 ? 0 : 1)}${units[index]}`
}

/** 取文件名扩展名 */
function getExtension(name = '') {
  const match = /\.([A-Za-z0-9]+)$/.exec(name)
  return match ? match[1].toLowerCase() : ''
}

/** 根据扩展名挑一个图标 */
function pickIcon(ext) {
  if (['doc', 'docx', 'dot', 'dotx', 'docm'].includes(ext)) return '📝'
  if (['xls', 'xlsx', 'xlsm', 'xlsb', 'csv'].includes(ext)) return '📊'
  if (['ppt', 'pptx', 'ppsx', 'potx'].includes(ext)) return '📽️'
  if (ext === 'pdf') return '📕'
  return '📄'
}

export default {
  name: 'FileViewerPreview',

  data() {
    return {
      acceptAttr: PREVIEW_ACCEPT_ATTR,
      shortcutExtensions: ['Word', 'Excel', 'PPT', 'PDF', 'Markdown', 'TXT', 'JSON'],
      // 示例文件：用远程公开链接，演示「URL 预览」能力
      samples: [
        { label: 'PDF 示例（W3C 规范片段）', value: 'https://mozilla.github.io/pdf.js/web/compressed.tracemonkey-pldi-09.pdf' },
        { label: 'DOCX 示例（Word 官方样张）', value: 'https://calibre-ebook.com/downloads/demos/demo.docx' },
        { label: 'PPTX 示例（Office 模板）', value: 'https://go.microsoft.com/fwlink/?linkid=2312232' },
      ],
      isDark: false,
      isDragOver: false,
      loading: false,
      loadingText: '正在解析文件…',
      errorMessage: '',
      currentFileName: '',
      currentFileSize: 0,
      urlInput: '',
      urlDialogVisible: false,
      urlDialogInput: '',
      viewerController: null,
      viewerPromise: null,
      assetBase: '',
    }
  },

  computed: {
    currentFileExt() {
      return getExtension(this.currentFileName)
    },

    currentFileExtLabel() {
      return this.currentFileExt ? this.currentFileExt.toUpperCase() : '未知格式'
    },

    currentFileIcon() {
      return pickIcon(this.currentFileExt)
    },

    currentFileSizeLabel() {
      return this.currentFileSize ? formatSize(this.currentFileSize) : ''
    },
  },

  mounted() {
    this.assetBase = resolveAssetBase()
    // 模块与静态资源都在 `/file-viewer/` 下，先告诉运行时加载器
    setFileViewerRuntimeBase(this.assetBase)
  },

  beforeDestroy() {
    this.destroyViewer()
  },

  methods: {
    /**
     * 懒创建查看器：第一次真正有文件要预览时才初始化，避免进页面就拉起渲染器。
     * core 与 renderer 都是浏览器原生 ESM，需要先异步 import 到位。
     * @returns {Promise<object|null>} ViewerController
     */
    ensureViewer() {
      if (this.viewerPromise) return this.viewerPromise
      if (!this.$refs.viewerHost) return Promise.resolve(null)

      this.loading = true
      this.loadingText = '正在加载预览引擎…'

      this.viewerPromise = ensureFileViewerRuntime({ assetBase: this.assetBase })
        .then(({ mountViewer, setDefaultFileViewerAssetBaseUrl, rendererPreset }) => {
          // 全局告诉 core：worker / wasm / 字体都在 `/file-viewer/` 下，避免它去猜脚本所在目录
          setDefaultFileViewerAssetBaseUrl(this.assetBase)

          // mountViewer(容器, 初始挂载参数, core 选项)；初始参数只给 options，
          // 具体文件在选好之后再通过 controller.load({ file | url }) 传入。
          // styleIsolation 用 shadow：查看器内建样式封装在 Shadow DOM 里，不会被后台的 Element UI 全局样式污染。
          this.viewerController = mountViewer(this.$refs.viewerHost, {
            options: {
              theme: this.isDark ? 'dark' : 'light',
              locale: 'zh-CN',
              styleIsolation: 'shadow',
              // 只注册本页面需要的几条链路；关掉自动预设，避免命中别的重型渲染器
              autoRenderers: false,
              renderers: rendererPreset,
              toolbar: {
                search: true,
                zoom: true,
                download: true,
                print: true,
                exportHtml: false,
                theme: false,
                order: ['search', 'zoom', 'print', 'download'],
              },
              search: true,
              spreadsheet: {
                worker: 'auto',
                textEncoding: 'auto',
              },
              presentation: {
                pptWorker: 'auto',
              },
              archive: {
                workerUrl: `${this.assetBase}vendor/libarchive/worker-bundle.js`,
              },
            },
          }, {
            onError: error => this.handleRenderError(error),
          })

          return this.viewerController
        })
        .catch(error => {
          // 失败后允许重试（例如资源刚好没部署完）
          this.viewerPromise = null
          this.handleRenderError(error)
          return null
        })

      return this.viewerPromise
    },

    destroyViewer() {
      this.viewerPromise = null
      if (!this.viewerController) return

      try {
        this.viewerController.destroy()
      } catch (error) {
        console.warn('销毁预览器失败', error)
      }

      this.viewerController = null
    },

    /**
     * 用 File 对象渲染（本地文件，不经过网络）。
     */
    renderFile(file) {
      if (!file) return

      this.currentFileName = file.name
      this.currentFileSize = file.size
      this.errorMessage = ''
      this.loading = true
      this.loadingText = '正在本地解析…'

      this.ensureViewer()
        .then(controller => {
          if (!controller) return null
          return Promise.resolve(controller.load({ file })).catch(error => this.handleRenderError(error))
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * 用 URL 渲染（在线文件，需要目标站点允许跨域）。
     */
    renderUrl(url, filename = '') {
      const trimmed = (url || '').trim()
      if (!trimmed) return

      const guessName = filename || decodeURIComponent(trimmed.split('?')[0].split('/').pop() || '在线文件')
      this.currentFileName = guessName
      this.currentFileSize = 0
      this.errorMessage = ''
      this.loading = true
      this.loadingText = '正在下载并解析…'

      this.ensureViewer()
        .then(controller => {
          if (!controller) return null
          return Promise.resolve(controller.load({ url: trimmed, filename: guessName }))
            .catch(error => this.handleRenderError(error))
        })
        .finally(() => {
          this.loading = false
        })
    },

    handleRenderError(error) {
      const message = (error && (error.message || error.toString())) || '未知错误'
      this.errorMessage = message
    },

    /**
     * el-upload 的 onChange：auto-upload 关掉后只拿到原生文件，直接交给查看器。
     */
    handleFileChange(file) {
      const raw = file && file.raw
      if (raw) this.renderFile(raw)
    },

    handleDrop(event) {
      this.isDragOver = false
      const files = event.dataTransfer && event.dataTransfer.files
      if (files && files.length) this.renderFile(files[0])
    },

    handleDragLeave(event) {
      // 只有真正离开预览区才取消高亮，避免子元素间移动闪烁
      if (event.currentTarget && event.currentTarget.contains(event.relatedTarget)) return
      this.isDragOver = false
    },

    handleSample(value) {
      this.renderUrl(value, value.split('/').pop() || '示例文件')
    },

    openUrl() {
      if (!this.urlInput.trim()) return
      this.renderUrl(this.urlInput)
      this.urlInput = ''
    },

    confirmUrlDialog() {
      const url = this.urlDialogInput.trim()
      if (!url) return
      this.urlDialogVisible = false
      this.urlDialogInput = ''
      this.renderUrl(url)
    },

    handleThemeChange(value) {
      const controller = this.viewerController
      if (!controller) return

      try {
        // 查看器支持运行时切换主题；失败则静默
        controller.update({ theme: value ? 'dark' : 'light' })
      } catch (error) {
        console.warn('切换主题失败', error)
      }
    },

    downloadCurrent() {
      const controller = this.viewerController
      if (!controller || !this.currentFileName) return

      try {
        controller.downloadOriginalFile()
      } catch (error) {
        console.warn('下载原文件失败', error)
        this.$toast.warning('当前来源不支持下载')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.file-preview-page {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  gap: 10px;
}

/* ---------- 顶部工具栏 ---------- */
.preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 0 0 auto;
  padding: 12px 16px;
  border-radius: 10px;
  background: var(--surface-1);
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
}

.toolbar-left {
  min-width: 0;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-icon {
  font-size: 24px;
  line-height: 1;
}

.brand-text {
  display: flex;
  flex-direction: column;

  strong {
    font-size: 15px;
    color: #1f2d3d;
  }

  small {
    margin-top: 2px;
    font-size: 12px;
    color: #8492a6;
  }
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.toolbar-upload {
  display: inline-flex;

  ::v-deep .el-upload {
    display: inline-flex;
  }
}

.toolbar-switch {
  display: flex;
  align-items: center;
  gap: 6px;

  .switch-label {
    font-size: 12px;
    color: #8492a6;
  }
}

/* ---------- 主体 ---------- */
.preview-body {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 10px;
}

.preview-aside {
  display: flex;
  flex: 0 0 232px;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  overflow-y: auto;
}

.aside-card {
  padding: 14px;
  border-radius: 10px;
  background: var(--surface-1);
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);

  &__title {
    margin-bottom: 10px;
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
  }

  &--hint {
    background: var(--surface-1);
  }
}

.aside-file {
  display: flex;
  align-items: center;
  gap: 10px;

  &__icon {
    flex: 0 0 auto;
    font-size: 26px;
    line-height: 1;
  }

  &__meta {
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    color: #1f2d3d;
    word-break: break-all;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__sub {
    margin-top: 2px;
    font-size: 12px;
    color: #8492a6;
  }
}

.aside-actions {
  margin-top: 8px;
}

.aside-empty {
  font-size: 12px;
  color: #a8abb2;
}

.tag-wall {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.format-tag {
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--surface-2);
  color: #4b6bfb;
  font-size: 12px;
  line-height: 18px;

  &--more {
    background: var(--surface-2);
    color: #8492a6;
  }
}

.aside-tip {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #8492a6;
}

.hint-list {
  margin: 0;
  padding-left: 16px;
  font-size: 12px;
  line-height: 1.9;
  color: #6b7a8f;

  code {
    padding: 1px 4px;
    border-radius: 4px;
    background: var(--surface-2);
    font-size: 11px;
  }
}

/* ---------- 预览区 ---------- */
.preview-stage {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-radius: 10px;
  background: var(--surface-2);
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.05);

  &.is-dark {
    background: #17191d;
  }

  &.is-dragover {
    box-shadow: inset 0 0 0 2px #4b6bfb;
  }
}

.viewer-host {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.stage-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  overflow-y: auto;
  background: rgba(245, 247, 251, 0.96);
}

.empty-icon {
  font-size: 44px;
  line-height: 1;
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2d3d;
}

.empty-sub {
  font-size: 12px;
  color: #8492a6;
}

.empty-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}

.empty-upload {
  display: inline-flex;

  ::v-deep .el-upload {
    display: inline-flex;
  }
}

.url-row {
  width: min(460px, 100%);
  margin-top: 4px;

  ::v-deep .el-input-group__append {
    background: #4b6bfb;
    border-color: #4b6bfb;
    color: #fff;
  }
}

.empty-flow {
  display: flex;
  align-items: stretch;
  gap: 10px;
  width: min(760px, 100%);
  margin-top: 16px;
}

.flow-card {
  position: relative;
  flex: 1;
  padding: 12px 12px 12px 14px;
  border-radius: 10px;
  background: var(--surface-1);
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);

  p {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.6;
    color: #8492a6;
  }
}

.flow-step {
  position: absolute;
  top: -8px;
  left: 12px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: #4b6bfb;
  color: #fff;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}

.flow-title {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2d3d;
}

.flow-arrow {
  align-self: center;
  color: #c0c4cc;
}

.stage-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(2px);
}

.preview-stage.is-dark .stage-loading {
  background: rgba(23, 25, 29, 0.72);
}

.loading-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #4b6bfb;
  box-shadow: 0 0 0 6px rgba(75, 107, 251, 0.15);
  animation: pulse 1.1s ease-in-out infinite;
}

.loading-text {
  font-size: 13px;
  color: #5c6b82;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(0.9);
    opacity: 0.7;
  }

  50% {
    transform: scale(1.1);
    opacity: 1;
  }
}

.stage-error {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(560px, calc(100% - 40px));
  padding: 20px;
  border-radius: 12px;
  background: var(--surface-1);
  box-shadow: 0 12px 32px rgba(24, 44, 64, 0.16);
  transform: translate(-50%, -50%);
}

.error-title {
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #f56c6c;
}

.error-message {
  font-size: 13px;
  line-height: 1.6;
  color: #1f2d3d;
  word-break: break-word;
}

.error-sub {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.6;
  color: #8492a6;
}

.stage-dragover-tip {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  color: #4b6bfb;
  background: rgba(75, 107, 251, 0.08);
  pointer-events: none;
}

.dialog-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #8492a6;
}

/* 中屏收窄左侧信息栏 */
@media (max-width: 1480px) {
  .preview-aside {
    flex-basis: 208px;
  }
}

@media (max-width: 1200px) {
  .preview-aside {
    display: none;
  }

  .empty-flow {
    flex-direction: column;
  }

  .flow-arrow {
    display: none;
  }
}
</style>
